// ============================================================
// Hero Armory — account-level hero progression
// ============================================================
//
// Separate from the per-run hero level (`hero.level`). Heroes gain Armory XP
// from each run (main-slot hero at 5 %, recruits at 1 % of their in-run XP)
// and unlock one passive-pick slot at Armory levels 10 / 20 / 30. Passives are
// situational always-on effects — charge-style openers, low-HP vampirism,
// kill-chain procs — meant to add depth without flat stat inflation.
//
// Run-start snapshot: when a run begins, each deployed hero's passive IDs are
// copied into `activePassiveIds` and locked for the duration of the run. This
// prevents "respec to match the biome" exploits while still letting the player
// browse the Armory and queue changes for next run.
/** Upper bound for a hero's Armory level — "mastery" tail extends well past the
 *  perk-unlock cap at level 30. The full 5000 is intentionally unreachable for
 *  all but the most dedicated runs; it's there as an ambition ceiling, not a
 *  grind target. Perks cap at level 30; everything past is prestige. */
export const ARMORY_MAX_LEVEL = 5000;
/** Armory levels at which the player gets a passive-pick choice. */
export const ARMORY_PASSIVE_UNLOCK_LEVELS = [10, 20, 30];
/** Maximum level at which perks can be slotted. Beyond this, the hero continues
 *  to level up (cosmetic / mastery) but unlocks no further perk picks. */
export const ARMORY_PASSIVE_MAX_LEVEL = 30;
/** Maximum passives a hero can have equipped simultaneously. */
export const ARMORY_MAX_PASSIVES = 3;
/** Flat rune cost to wipe all passive picks on a single hero. Currently 0 —
 *  respec was made free so players can experiment with perk builds without a
 *  rune penalty. Kept as a constant (not hard-coded 0) in case a future patch
 *  wants to reintroduce a cost. */
export const ARMORY_RESPEC_COST_RUNES = 0;
/** Fraction of in-run XP the slot-0 (main) hero converts into Armory XP.
 *  Tuned aggressively low so a full 100-stage run nets ~2-3 Armory levels
 *  for the main hero. Iterate via this single constant. */
export const ARMORY_MAIN_XP_SHARE = 0.005;
/** Fraction of in-run XP each recruited hero converts into Armory XP. */
export const ARMORY_RECRUIT_XP_SHARE = 0.001;
// ── XP curve ─────────────────────────────────────────────────────────────
//
// Front-loaded exponential: reaching the L 30 perk cap is a *major* commitment,
// and the prestige tail after that is essentially flat with a tiny drift.
//
//   Phase 1 (L  1-30):   1.17×/level — the real progression curve. L 30 now
//                        costs ~9.4 k XP (vs ~1.6 k on the old 1.10 curve),
//                        and the total journey 1→30 is ~4× harder than before
//                        so each perk unlock feels earned rather than routine.
//   Phase 2 (L 31-100):  1.01×/level — post-perk mastery, barely rising. Keeps
//                        the "you're still progressing" signal without pretending
//                        each level is a fresh investment.
//   Phase 3 (L 100+):    flat at the L 100 value. Bounds L 5000 so Number.MAX
//                        is never a concern.
const ARMORY_XP_BASE = 100;
const ARMORY_XP_PHASE1_GROWTH = 1.17;
const ARMORY_XP_PHASE2_GROWTH = 1.01;
const ARMORY_XP_PHASE2_START = 30; // first level using phase-2 growth
const ARMORY_XP_CAP_LEVEL = 100; // growth freezes at this level
/** XP required to go from `level` → `level + 1`. */
export function armoryXpToNext(level) {
    if (level >= ARMORY_MAX_LEVEL)
        return Infinity;
    const clamped = Math.min(level, ARMORY_XP_CAP_LEVEL);
    if (clamped < ARMORY_XP_PHASE2_START) {
        return Math.round(ARMORY_XP_BASE * Math.pow(ARMORY_XP_PHASE1_GROWTH, Math.max(0, clamped - 1)));
    }
    const phase1End = ARMORY_XP_BASE * Math.pow(ARMORY_XP_PHASE1_GROWTH, ARMORY_XP_PHASE2_START - 1);
    return Math.round(phase1End * Math.pow(ARMORY_XP_PHASE2_GROWTH, clamped - ARMORY_XP_PHASE2_START));
}
// Cumulative XP lookup cached — `resolveArmoryLevel` used to re-iterate 1..level
// on every call which scaled as O(level²). With MAX_LEVEL now 5000 that was
// untenable; precompute once at first use.
let _cumXpCache = null;
function ensureCumXpCache() {
    if (_cumXpCache)
        return _cumXpCache;
    const arr = new Array(ARMORY_MAX_LEVEL + 1);
    arr[0] = 0;
    arr[1] = 0;
    for (let L = 2; L <= ARMORY_MAX_LEVEL; L++) {
        arr[L] = arr[L - 1] + armoryXpToNext(L - 1);
    }
    _cumXpCache = arr;
    return arr;
}
/** Cumulative XP required to reach `level` from 0. Useful for level-up math. */
export function armoryTotalXpForLevel(level) {
    const cache = ensureCumXpCache();
    const clamped = Math.min(Math.max(level, 0), ARMORY_MAX_LEVEL);
    return cache[clamped] ?? 0;
}
/** Given current level + accumulated XP, compute the level the hero *should* be.
 *  Handles multi-level-ups in a single XP grant. */
export function resolveArmoryLevel(armoryXp, currentLevel) {
    let level = Math.max(1, currentLevel);
    while (level < ARMORY_MAX_LEVEL && armoryXp >= armoryTotalXpForLevel(level + 1)) {
        level++;
    }
    return level;
}
/** Count of passive slots the hero has unlocked at this level. */
export function unlockedPassiveSlots(level) {
    let slots = 0;
    for (const threshold of ARMORY_PASSIVE_UNLOCK_LEVELS) {
        if (level >= threshold)
            slots++;
    }
    return slots;
}
/** All authored hero passives. Phase 1 ships Goomar (warrior) + Lumiel
 *  (healer). Quest-unlocked passives added later will live here too. */
export const PASSIVE_DEFS = {
    // ── Goomar (warrior) ─────────────────────────────────────────────────
    passive_goomar_last_stand: {
        id: 'passive_goomar_last_stand', heroClass: 'warrior',
        name: 'Last Stand', icon: 'ᚦ', shortTag: 'LIFE',
        description: 'Below 25 % HP, gain 15 % life-steal on all attacks.',
    },
    passive_goomar_crushing_momentum: {
        id: 'passive_goomar_crushing_momentum', heroClass: 'warrior',
        name: 'Crushing Momentum', icon: 'ᛏ', shortTag: 'PEN',
        description: 'After each kill, the next attack deals +10 % armour penetration (consumed on use).',
    },
    passive_goomar_second_wind: {
        id: 'passive_goomar_second_wind', heroClass: 'warrior',
        name: 'Second Wind', icon: 'ᚨ', shortTag: 'HEAL',
        description: 'First time HP drops below 20 % each wave, heal 20 % max HP. Once per wave.',
    },
    passive_goomar_bloodied_resolve: {
        id: 'passive_goomar_bloodied_resolve', heroClass: 'warrior',
        name: 'Bloodied Resolve', icon: 'ᛞ', shortTag: 'DMG',
        description: '+1 % attack damage per 10 % missing HP (caps at +8 %).',
    },
    passive_goomar_giants_bane: {
        id: 'passive_goomar_giants_bane', heroClass: 'warrior',
        name: "Giant's Bane", icon: 'ᚼ', shortTag: 'OPEN',
        description: '+10 % damage against enemies at full HP (rewards opening strikes).',
    },
    passive_goomar_berserker_rush: {
        id: 'passive_goomar_berserker_rush', heroClass: 'warrior',
        name: 'Berserker Rush', icon: 'ᛉ', shortTag: 'CHARGE',
        description: 'Every 18 s, Goomar leaps to the nearest enemy for a crushing strike (1.6× ATK on landing). Requires a front-row slot.',
    },
    passive_goomar_enrage: {
        id: 'passive_goomar_enrage', heroClass: 'warrior',
        name: 'Enrage', icon: 'ᚱ', shortTag: 'ENRAGE',
        description: 'When rage maxes, Goomar enters a 10 s frenzy — every auto-attack is a guaranteed critical hit. Rage resets on activation.',
    },
    // ── Lumiel (healer) ──────────────────────────────────────────────────
    passive_lumiel_aegis_shield: {
        id: 'passive_lumiel_aegis_shield', heroClass: 'healer',
        name: 'Aegis Shield', icon: 'ᚷ', shortTag: 'SHIELD',
        description: 'First heal each encounter also grants the target a 150 HP shield for 5 s.',
    },
    passive_lumiel_chain_of_grace: {
        id: 'passive_lumiel_chain_of_grace', heroClass: 'healer',
        name: 'Chain of Grace', icon: 'ᛇ', shortTag: 'CHAIN',
        description: 'When Lumiel heals while below 40 % HP herself, 40 % of the heal splashes to the nearest ally.',
    },
    passive_lumiel_divine_intervention: {
        id: 'passive_lumiel_divine_intervention', heroClass: 'healer',
        name: 'Divine Intervention', icon: 'ᛉ', shortTag: 'SAVE',
        description: 'Once per encounter, if an ally would die they survive at 1 HP and heal 30 % max HP over 2 s. 180 s cooldown after trigger.',
    },
    passive_lumiel_purifying_light: {
        id: 'passive_lumiel_purifying_light', heroClass: 'healer',
        name: 'Purifying Light', icon: 'ᛊ', shortTag: 'TICK',
        description: 'Every 15 s, all allies recover (20 + 0.5×SP) HP from a sanctified pulse.',
    },
    passive_lumiel_martyrdom: {
        id: 'passive_lumiel_martyrdom', heroClass: 'healer',
        name: 'Martyrdom', icon: 'ᛟ', shortTag: 'SHARE',
        description: '20 % of damage Lumiel takes becomes healing on the lowest-HP ally.',
    },
    // ── Ironheld (tank) ──────────────────────────────────────────────────
    // Values tuned ~50 % softer than the first drafts — closer to a "small
    // edge" than a power swing.
    passive_ironheld_unyielding_bulwark: {
        id: 'passive_ironheld_unyielding_bulwark', heroClass: 'tank',
        name: 'Unyielding Bulwark', icon: 'ᛟ', shortTag: 'BULWARK',
        description: 'When an incoming attack exceeds Ironheld\'s current shield, gain 15 % damage reduction for 2 s. 12 s cooldown.',
    },
    passive_ironheld_sanctified_duty: {
        id: 'passive_ironheld_sanctified_duty', heroClass: 'tank',
        name: 'Sanctified Duty', icon: 'ᛉ', shortTag: 'GUARD',
        description: 'While Challenge is active, all other allies gain 10 % damage reduction for the taunt duration.',
    },
    passive_ironheld_retributive_faith: {
        id: 'passive_ironheld_retributive_faith', heroClass: 'tank',
        name: 'Retributive Faith', icon: 'ᚷ', shortTag: 'REFLECT',
        description: 'Each attack Ironheld\'s shield fully absorbs reflects 10 % of the blocked damage back.',
    },
    passive_ironheld_bulwark_of_devotion: {
        id: 'passive_ironheld_bulwark_of_devotion', heroClass: 'tank',
        name: 'Bulwark of Devotion', icon: 'ᛒ', shortTag: 'REFLECT',
        description: 'Reflect 5 % of all damage Ironheld takes back to the attacker.',
    },
    passive_ironheld_aegis_of_light: {
        id: 'passive_ironheld_aegis_of_light', heroClass: 'tank',
        name: 'Aegis of Light', icon: 'ᛞ', shortTag: 'AEGIS',
        description: 'First time HP drops below 30 % per encounter: gain a (100 + SP) HP shield for 4 s.',
    },
    passive_ironheld_retaliatory_strike: {
        id: 'passive_ironheld_retaliatory_strike', heroClass: 'tank',
        name: 'Retaliatory Strike', icon: 'ᚦ', shortTag: 'RETAL',
        description: 'When struck, 25 % chance to swing back immediately for 1.5× melee damage. Finally gives Ironheld a real damage hook — the more he tanks, the more he hits back.',
    },
    // ── Zyphara (wizard) ────────────────────────────────────────────────
    passive_zyphara_arcane_overflow: {
        id: 'passive_zyphara_arcane_overflow', heroClass: 'wizard',
        name: 'Arcane Overflow', icon: 'ᚨ', shortTag: 'OVERFLOW',
        description: 'While at full mana, the next spell cast deals +20 % damage (consumed on cast).',
    },
    passive_zyphara_cinder_chain: {
        id: 'passive_zyphara_cinder_chain', heroClass: 'wizard',
        name: 'Cinder Chain', icon: 'ᚦ', shortTag: 'SPLASH',
        description: 'Enemies killed by Zyphara\'s fire spells explode for 20 % of that spell\'s damage to enemies within 120 px.',
    },
    passive_zyphara_mana_siphon: {
        id: 'passive_zyphara_mana_siphon', heroClass: 'wizard',
        name: 'Mana Siphon', icon: 'ᚺ', shortTag: 'MANA',
        description: 'Being hit restores 1 % max mana (at most once per second).',
    },
    passive_zyphara_pyromancer_momentum: {
        id: 'passive_zyphara_pyromancer_momentum', heroClass: 'wizard',
        name: "Pyromancer's Momentum", icon: 'ᛊ', shortTag: 'CDR',
        description: 'Each spell cast reduces the cooldown of Zyphara\'s other spells by 0.25 s.',
    },
    passive_zyphara_elemental_insight: {
        id: 'passive_zyphara_elemental_insight', heroClass: 'wizard',
        name: 'Elemental Insight', icon: 'ᛉ', shortTag: 'OPENER',
        description: 'The first spell Zyphara casts at the start of each wave deals +15 % damage.',
    },
    // ── Lootfi (warrior — dwarf variant) ─────────────────────────────────
    passive_lootfi_gold_rush: {
        id: 'passive_lootfi_gold_rush', heroClass: 'warrior', heroKey: 'dwarf',
        name: 'Gold Rush', icon: 'ᚠ', shortTag: 'GOLD',
        description: 'Kills grant +1 % extra gold this run (caps at +15 % per run).',
    },
    passive_lootfi_stout_heart: {
        id: 'passive_lootfi_stout_heart', heroClass: 'warrior', heroKey: 'dwarf',
        name: 'Stout Heart', icon: 'ᚦ', shortTag: 'SURVIVE',
        description: 'First lethal hit per encounter leaves Lootfi at 1 HP instead. 120 s cooldown after trigger.',
    },
    passive_lootfi_mountain_roots: {
        id: 'passive_lootfi_mountain_roots', heroClass: 'warrior', heroKey: 'dwarf',
        name: 'Mountain Roots', icon: 'ᛏ', shortTag: 'ROOTED',
        description: 'After standing still 10 s+: +15 % damage and +5 % damage reduction until Lootfi moves.',
    },
    passive_lootfi_thrown_expertise: {
        id: 'passive_lootfi_thrown_expertise', heroClass: 'warrior', heroKey: 'dwarf',
        name: 'Thrown Expertise', icon: 'ᛗ', shortTag: 'THROW',
        description: 'Lootfi\'s mace throws deal +15 % damage and can ricochet once to a nearby enemy.',
    },
    passive_lootfi_dwarven_resolve: {
        id: 'passive_lootfi_dwarven_resolve', heroClass: 'warrior', heroKey: 'dwarf',
        name: 'Dwarven Resolve', icon: 'ᛝ', shortTag: 'STUN',
        description: 'When hit, 4 % chance to stun the attacker for 1 s. The same foe cannot be stunned again for 1.5 s.',
    },
    passive_lootfi_dwarven_charge: {
        id: 'passive_lootfi_dwarven_charge', heroClass: 'warrior', heroKey: 'dwarf',
        name: 'Dwarven Charge', icon: 'ᛉ', shortTag: 'CHARGE',
        description: 'Every 24 s, Lootfi barrels into the nearest enemy (1.9× ATK landing) and shockwaves nearby foes for 80 % of the hit. Requires a front-row slot.',
    },
    passive_lootfi_forgefire_strikes: {
        id: 'passive_lootfi_forgefire_strikes', heroClass: 'warrior', heroKey: 'dwarf',
        name: 'Forgefire Strikes', icon: 'ᚲ', shortTag: 'CLEAVE',
        description: 'Every 5th auto-attack ignites Lootfi\'s mace — the strike cleaves to nearby foes within 120 px for 60 % of the damage dealt.',
    },
    // ── Valka (warrior — valkyrie variant) ───────────────────────────────
    passive_valka_warmaidens_grit: {
        id: 'passive_valka_warmaidens_grit', heroClass: 'warrior', heroKey: 'valkyrie',
        name: "Warmaiden's Grit", icon: 'ᛟ', shortTag: 'HEALTH',
        description: 'Valka\'s max HP is raised by +15 % for the entire run.',
    },
    passive_valka_shieldbearers_oath: {
        id: 'passive_valka_shieldbearers_oath', heroClass: 'warrior', heroKey: 'valkyrie',
        name: "Shieldbearer's Oath", icon: 'ᚦ', shortTag: 'BULWARK',
        description: 'Shield Bash absorb grows to 40 % maxHP (was 25 %) and any damage the shield blocks is reflected at the attacker for 10 %.',
    },
    passive_valka_rally_standard: {
        id: 'passive_valka_rally_standard', heroClass: 'warrior', heroKey: 'valkyrie',
        name: 'Rally Standard', icon: 'ᚨ', shortTag: 'CRIT',
        description: 'Banner of Valor additionally grants allies in range +30 % critical damage.',
    },
    passive_valka_crushing_edge: {
        id: 'passive_valka_crushing_edge', heroClass: 'warrior', heroKey: 'valkyrie',
        name: 'Crushing Edge', icon: 'ᚲ', shortTag: 'PIERCE',
        description: 'Tempest Slam gains +15 % armour penetration per unique enemy struck during the preceding spin.',
    },
    passive_valka_plains_veteran: {
        id: 'passive_valka_plains_veteran', heroClass: 'warrior', heroKey: 'valkyrie',
        name: 'Plains Veteran', icon: 'ᛊ', shortTag: 'COUNTER',
        description: 'Counter-Strike retaliation damage grows from 80 % ATK to 120 % ATK.',
    },
    passive_valka_last_rally: {
        id: 'passive_valka_last_rally', heroClass: 'warrior', heroKey: 'valkyrie',
        name: 'Last Rally', icon: 'ᛉ', shortTag: 'SURVIVE',
        description: 'When Valka first drops below 30 % HP, allies in range gain +30 defence and a fresh 10 s banner buff. Triggers once per encounter.',
    },
    passive_valka_battle_fury: {
        id: 'passive_valka_battle_fury', heroClass: 'warrior', heroKey: 'valkyrie',
        name: 'Battle Fury', icon: 'ᛗ', shortTag: 'FURY',
        description: 'When rage maxes, Valka roars into Battle Fury for 8 s — +35 % attack speed. Rage resets on activation.',
    },
    // ── Sylvari (archer) ────────────────────────────────────────────────
    passive_sylvari_eagle_eye: {
        id: 'passive_sylvari_eagle_eye', heroClass: 'archer',
        name: 'Eagle Eye', icon: 'ᛖ', shortTag: 'BOSS',
        description: 'Attacks against bosses and elite enemies deal +5 % damage.',
    },
    passive_sylvari_hunters_focus: {
        id: 'passive_sylvari_hunters_focus', heroClass: 'archer',
        name: "Hunter's Focus", icon: 'ᚷ', shortTag: 'FOCUS',
        description: 'Every 5th consecutive attack on the same target deals 3× damage.',
    },
    passive_sylvari_piercing_volley: {
        id: 'passive_sylvari_piercing_volley', heroClass: 'archer',
        name: 'Piercing Volley', icon: 'ᛞ', shortTag: 'PIERCE',
        description: "Sylvari's Multishot ignores 50 % of enemy armour.",
    },
    passive_sylvari_wild_step: {
        id: 'passive_sylvari_wild_step', heroClass: 'archer',
        name: 'Wild Step', icon: 'ᛒ', shortTag: 'EVADE',
        description: 'After moving, gain +7 % dodge chance and +15 % attack speed for 3 s.',
    },
    passive_sylvari_forests_favor: {
        id: 'passive_sylvari_forests_favor', heroClass: 'archer',
        name: "Forest's Favor", icon: 'ᚹ', shortTag: 'DODGE',
        description: 'Sylvari has a permanent 25 % chance to dodge incoming attacks.',
    },
    // ── Thorne (druid) ──────────────────────────────────────────────────
    // First-pass drafts — situational, lean into the bear/human dual-mode + the
    // moonfire stack mechanic Thornlash got. Numbers ready for tuning.
    passive_thorne_bramble_grasp: {
        id: 'passive_thorne_bramble_grasp', heroClass: 'druid',
        name: 'Bramble Grasp', icon: 'ᚦ', shortTag: 'PULL',
        description: 'Wild Growth\'s root pulls each enemy 30 px closer to Thorne for the duration.',
    },
    passive_thorne_bear_resilience: {
        id: 'passive_thorne_bear_resilience', heroClass: 'druid',
        name: 'Bear Resilience', icon: 'ᛒ', shortTag: 'BEAR',
        description: 'While Bear Form is active, Thorne gains an additional 10 % damage reduction.',
    },
    passive_thorne_natures_cycle: {
        id: 'passive_thorne_natures_cycle', heroClass: 'druid',
        name: "Nature's Cycle", icon: 'ᛇ', shortTag: 'MANA',
        description: 'When a Thornlash hit kills its target, Thorne regenerates 5 % of his max mana.',
    },
    passive_thorne_verdant_tide: {
        id: 'passive_thorne_verdant_tide', heroClass: 'druid',
        name: 'Verdant Tide', icon: 'ᚷ', shortTag: 'HEAL',
        description: 'Regrowth heals an extra 10 % per ally currently below 30 % HP (caps at +30 %).',
    },
    passive_thorne_lunar_reverence: {
        id: 'passive_thorne_lunar_reverence', heroClass: 'druid',
        name: 'Lunar Reverence', icon: 'ᛟ', shortTag: 'STACK',
        description: 'Moonfire (human-form Thornlash) can stack up to 5 times instead of 3.',
    },
    passive_thorne_swift_growth: {
        id: 'passive_thorne_swift_growth', heroClass: 'druid',
        name: 'Swift Growth', icon: 'ᛜ', shortTag: 'CDR',
        description: 'Wild Growth re-erupts twice as fast (cooldown halved).',
    },
    // ── Zayla (archer / amazon) ─────────────────────────────────────────
    passive_zayla_skybound_strike: {
        id: 'passive_zayla_skybound_strike', heroClass: 'archer', heroKey: 'amazon',
        name: 'Skybound Strike', icon: 'ᛏ', shortTag: 'LEAP',
        description: 'Leap Strike deals +20 % landing damage when only one enemy is in the impact area.',
    },
    passive_zayla_frost_bite: {
        id: 'passive_zayla_frost_bite', heroClass: 'archer', heroKey: 'amazon',
        name: 'Frost Bite', icon: 'ᛁ', shortTag: 'FREEZE',
        description: 'Freezing Shot freezes the target for an additional 1 s.',
    },
    passive_zayla_battle_resonance: {
        id: 'passive_zayla_battle_resonance', heroClass: 'archer', heroKey: 'amazon',
        name: 'Battle Resonance', icon: 'ᚺ', shortTag: 'CRY',
        description: 'War Cry also restores 10 % max mana to all allies on cast.',
    },
    passive_zayla_spear_mastery: {
        id: 'passive_zayla_spear_mastery', heroClass: 'archer', heroKey: 'amazon',
        name: 'Spear Mastery', icon: 'ᛜ', shortTag: 'VOLLEY',
        description: 'Spear Volley triggers every 7th attack instead of every 10th.',
    },
    passive_zayla_rangers_distance: {
        id: 'passive_zayla_rangers_distance', heroClass: 'archer', heroKey: 'amazon',
        name: "Ranger's Distance", icon: 'ᚷ', shortTag: 'RANGE',
        description: 'Auto-attacks deal +1 % damage per 50 px of distance to the target (caps at +20 %).',
    },
    // ── Kael (assassin) ──────────────────────────────────────────────────
    passive_kael_lethal_tempo: {
        id: 'passive_kael_lethal_tempo', heroClass: 'assassin',
        name: 'Lethal Tempo', icon: 'ᛏ', shortTag: 'TEMPO',
        description: 'Each consecutive auto-attack on the same target adds +2 % damage (caps at +10 %, resets when Kael switches target).',
    },
    passive_kael_shadowed_steps: {
        id: 'passive_kael_shadowed_steps', heroClass: 'assassin',
        name: 'Shadowed Steps', icon: 'ᛇ', shortTag: 'CRIT',
        description: 'Dirty Blades is always a guaranteed critical hit.',
    },
    passive_kael_venomous_strike: {
        id: 'passive_kael_venomous_strike', heroClass: 'assassin',
        name: 'Venomous Strike', icon: 'ᚦ', shortTag: 'VENOM',
        description: 'Sinister Strike\'s poison DoT lasts an additional 3 s.',
    },
    passive_kael_energy_surge: {
        id: 'passive_kael_energy_surge', heroClass: 'assassin',
        name: 'Energy Surge', icon: 'ᛊ', shortTag: 'ENERGY',
        description: 'Killing an enemy restores 15 energy. Internal cooldown of 3 s.',
    },
    passive_kael_phantom_reflexes: {
        id: 'passive_kael_phantom_reflexes', heroClass: 'assassin',
        name: 'Phantom Reflexes', icon: 'ᛒ', shortTag: 'REFLEX',
        description: 'While Evasion is active, each hit Kael takes adds +5 % damage on his next attacks for 5 s (stacks up to +20 %).',
    },
    // ── Morveth (wizard / necromancer) ──────────────────────────────────
    // Builds around soul count, minions, and the thin line between
    // death and resurrection — not pure DPS like Zyphara. `heroKey: 'necromancer'`
    // matches the strict-lookup rule in getPassivesForHero so these don't leak
    // to base-class wizards.
    passive_morveth_soul_harvest: {
        id: 'passive_morveth_soul_harvest', heroClass: 'wizard', heroKey: 'necromancer',
        name: 'Soul Harvest', icon: 'ᛉ', shortTag: 'SOULS',
        description: 'Critical kills grant +2 souls instead of +1 — turns crit into a resource engine for Bone Shield scaling.',
    },
    passive_morveth_undying_legion: {
        id: 'passive_morveth_undying_legion', heroClass: 'wizard', heroKey: 'necromancer',
        name: 'Undying Legion', icon: 'ᛟ', shortTag: 'MINIONS',
        description: 'Raised undead gain +5 % max HP and +5 % ATK per current soul (hard-capped at 20 souls = +100 %).',
    },
    passive_morveth_necrotic_aura: {
        id: 'passive_morveth_necrotic_aura', heroClass: 'wizard', heroKey: 'necromancer',
        name: 'Necrotic Aura', icon: 'ᚨ', shortTag: 'AURA',
        description: 'Every 5 s, all enemies within 200 px take pure damage equal to (soul count × 8). No cast, no cooldown management.',
    },
    passive_morveth_bone_grip: {
        id: 'passive_morveth_bone_grip', heroClass: 'wizard', heroKey: 'necromancer',
        name: 'Bone Grip', icon: 'ᛒ', shortTag: 'SHIELD',
        description: 'Bone Shield absorbs +25 %. When the shield breaks it explodes for 40 % of the consumed HP to enemies within 180 px.',
    },
    passive_morveth_death_pact: {
        id: 'passive_morveth_death_pact', heroClass: 'wizard', heroKey: 'necromancer',
        name: 'Death Pact', icon: 'ᛞ', shortTag: 'REVIVE',
        description: 'On death, if Morveth has ≥ 10 souls, consumes them all and revives with (souls × 5 % max HP). Once per run.',
    },
    // ── Cyrith (wizard / sorcerer) ───────────────────────────────────────────
    // Battlemage hybrid — perks that reward summon synergy or hybrid stat
    // builds. `heroKey: 'sorcerer'` matches the strict (heroClass, heroKey)
    // lookup so these never leak to base wizards or Morveth.
    passive_cyrith_conduit_bond: {
        id: 'passive_cyrith_conduit_bond', heroClass: 'wizard', heroKey: 'sorcerer',
        name: 'Conduit Bond', icon: 'ᚹ', shortTag: 'BOND',
        description: 'Each Vorrak auto-attack feeds Cyrith — her next ability deals +20% damage per stack (max 3 stacks, consumed on cast).',
    },
    passive_cyrith_storm_tempered: {
        id: 'passive_cyrith_storm_tempered', heroClass: 'wizard', heroKey: 'sorcerer',
        name: 'Storm-Tempered', icon: 'ᛉ', shortTag: 'ARC',
        description: 'Storm Bolt arcs to a second nearby foe within 140 px for 50% of its damage.',
    },
    passive_cyrith_anchors_gift: {
        id: 'passive_cyrith_anchors_gift', heroClass: 'wizard', heroKey: 'sorcerer',
        name: "Anchor's Gift", icon: 'ᚷ', shortTag: 'FIELD',
        description: 'Static Field also heals every ally inside its radius for 1% max HP per tick.',
    },
    passive_cyrith_forge_of_lightning: {
        id: 'passive_cyrith_forge_of_lightning', heroClass: 'wizard', heroKey: 'sorcerer',
        name: 'Forge of Lightning', icon: 'ᚲ', shortTag: 'RESET',
        description: 'When Vorrak dies, the next Forge Vorrak cast is free (0 mana) and instant (no cooldown).',
    },
    passive_cyrith_spellblade: {
        id: 'passive_cyrith_spellblade', heroClass: 'wizard', heroKey: 'sorcerer',
        name: 'Spellblade', icon: 'ᛗ', shortTag: 'BLADE',
        description: 'Arc Strike\'s cooldown drops by 1 s for each foe struck by Storm Bolt or Static Field this encounter.',
    },
    passive_cyrith_stormcoil: {
        id: 'passive_cyrith_stormcoil', heroClass: 'wizard', heroKey: 'sorcerer',
        name: 'Stormcoil', icon: 'ᛏ', shortTag: 'CHAIN',
        description: 'At full mana, every 4th auto-attack chains lightning to up to 3 enemies for 60% of the swing damage.',
    },
};
/** All passives available to a given hero class / key, ordered by id.
 *  Strict matching: a hero's passive pool is determined by `(heroClass, heroKey)`
 *  as a pair. "No heroKey on the def" means "for the base-class hero only" —
 *  it does NOT fall through to every hero of that class. Without this strict
 *  compare, Morveth (wizard, heroKey='necromancer') would inherit Zyphara's
 *  (wizard, no heroKey) passives — a real bug flagged by the playtester. */
export function getPassivesForHero(heroClass, heroKey) {
    const results = [];
    const wanted = heroKey ?? null;
    for (const def of Object.values(PASSIVE_DEFS)) {
        if (def.heroClass !== heroClass)
            continue;
        if ((def.heroKey ?? null) !== wanted)
            continue;
        results.push(def);
    }
    return results.sort((a, b) => a.id.localeCompare(b.id));
}
/** Lookup helper — returns undefined if no such passive is authored. */
export function getPassiveDef(id) {
    if (!id)
        return undefined;
    return PASSIVE_DEFS[id];
}
/** Default / empty Armory state for a hero not yet tracked in `armoryByHero`. */
export function emptyArmoryHeroState() {
    return {
        level: 1, xp: 0, passiveIds: [],
        journal: { runsCompleted: 0, highestStage: 0, totalKills: 0, totalBossKills: 0 },
    };
}
/** Frame-ornament tier driven by Armory level. Returned as an integer 0..8
 *  so scenes can index into a cosmetic table without knowing the breakpoints.
 *  Milestones match the design doc: 10 / 20 / 30 / 50 / 100 / 200 / 500 / 1000. */
export function armoryFrameTier(level) {
    if (level >= 1000)
        return 8;
    if (level >= 500)
        return 7;
    if (level >= 200)
        return 6;
    if (level >= 100)
        return 5;
    if (level >= 50)
        return 4;
    if (level >= 30)
        return 3;
    if (level >= 20)
        return 2;
    if (level >= 10)
        return 1;
    return 0;
}
//# sourceMappingURL=armory.js.map